/**
 * Condition summary as one scope sees it.
 * For Media Devoted | Created By Ciro Autiero
 *
 * In the default view the summary keeps its `(australia:202, ▸a1b2…:16)`
 * suffix, which is the only place overrides are visible there. Inside a scope
 * that suffix is redundant AND misleading — the row already answers "what does
 * France use?", so it shows that number alone plus where it came from.
 */

import {
  isDefaultScope,
  resolveForScope,
  type TagRuleScope,
  type ValueSource,
} from '@/features/tag-rule-scope';
import type { TagRuleCondition } from '@/hooks';

export interface ScopedConditionSummary {
  text: string;
  /** Undefined in the default view — nothing to badge there. */
  state?: ValueSource;
}

/**
 * `formatBase` renders "FTD ≥ 14" for a condition whose value has already been
 * swapped for the scope's. Passing it in keeps this module free of the metric
 * label tables, which sit behind a barrel that cannot be imported from tests.
 */
export function formatScopedCondition(
  c: TagRuleCondition,
  scope: TagRuleScope | undefined,
  formatBase: (c: TagRuleCondition) => string,
): ScopedConditionSummary {
  if (isDefaultScope(scope) || !scope) return { text: formatBase(c) };
  const { value, source, compareTo } = resolveForScope(c, scope);
  // Strip the override lists before formatting: they would re-append the
  // suffix this view exists to replace. The resolved tier's comparison (or
  // its absence — a pinned number replaces even a base comparison) rides in.
  const asScope: TagRuleCondition = {
    ...c,
    value,
    compareTo,
    countryThresholds: undefined,
    campaignThresholds: undefined,
  };
  return { text: formatBase(asScope), state: source };
}
